<template>
  <div class="login-page-wrapper relative min-h-screen overflow-hidden">
    <!-- Dynamic Form Styling -->
    <component :is="'style'" v-if="dynamicTextStyles">{{
      dynamicTextStyles
    }}</component>

    <!-- Loading overlay (background config) -->
    <div
      v-if="!isBackgroundReady"
      class="fixed inset-0 bg-black z-50 flex items-center justify-center"
    >
      <div class="flex flex-col items-center gap-4">
        <div
          class="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin"
        ></div>
        <p class="text-white/80 text-sm">Carregando...</p>
      </div>
    </div>

    <!-- Solid gradient background -->
    <div class="auth-background" :style="customBackgroundStyle"></div>

    <!-- Subtle overlay for depth -->
    <div class="auth-overlay"></div>

    <!-- Content layer -->
    <div
      class="login-glass relative z-10 min-h-screen flex flex-col px-6 py-10 pb-6 sm:py-12 sm:pb-8 lg:px-0 lg:py-0"
    >
      <!-- TOP: Theme toggle -->
      <div
        class="flex items-center justify-end w-full max-w-sm mx-auto lg:max-w-none lg:px-12 lg:pt-8 lg:pb-4"
      >
        <div class="flex items-center gap-2">
          <BackgroundCustomizer
            ref="backgroundCustomizer"
            :nomeEmpresa="nomeEmpresa"
            :showControls="showBackgroundCustomizer"
            @background-change="handleBackgroundChange"
            @button-color-change="handleButtonColorChange"
            @form-styling-change="handleFormStyling"
          />

          <button
            @click="handleThemeClick"
            class="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Toggle theme"
            :aria-pressed="isDark"
          >
            <svg
              v-if="!isDark"
              class="w-5 h-5 text-yellow-300"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
              />
            </svg>
            <svg
              v-else
              class="w-5 h-5 text-slate-300"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"
              />
            </svg>
          </button>

          <!-- Secret input for editor mode activation -->
          <input
            v-if="showSecretInput"
            v-model="secretInput"
            @input="checkSecretCode"
            ref="secretInput"
            type="text"
            class="fixed -left-full opacity-0 w-0 h-0"
            autocomplete="off"
          />
        </div>
      </div>

      <!-- CENTER -->
      <div
        class="flex-1 flex flex-col justify-center w-full pb-8"
        :class="parentContainerClass"
        :style="parentContainerStyle"
      >
        <div
          class="form-container"
          :class="[formContainerClass, childContainerWidthClass]"
          :style="formContainerStyle"
        >
          <!-- Logo com backdrop -->
          <div class="flex justify-center mb-6">
            <div
              :class="[
                'inline-flex min-w-0 max-w-full items-center justify-center px-3 py-2 rounded-xl',
                { 'bg-black/10': !isFormStylingActive },
              ]"
            >
              <CompanyLogo
                :nome-empresa="nomeEmpresa"
                :is-dark="isDark"
                :invert="shouldInvertLogo"
                variant="auth"
                @touchstart="startLogoPress"
                @touchend="endLogoPress"
                @touchcancel="endLogoPress"
              />
            </div>
          </div>

          <!-- Título com backdrop -->
          <div class="flex justify-center mb-2">
            <div
              :class="[
                'inline-flex px-3 py-1.5 rounded-xl',
                { 'bg-black/10': !isFormStylingActive },
              ]"
            >
              <h2 class="text-2xl font-semibold text-center text-white">
                Cadastre-se
              </h2>
            </div>
          </div>

          <div class="flex justify-center mb-6">
            <div
              :class="[
                'inline-flex px-3 py-1 rounded-lg',
                { 'bg-black/10': !isFormStylingActive },
              ]"
            >
              <p class="text-sm text-center text-white/60">
                Defina sua senha de acesso
              </p>
            </div>
          </div>

          <!-- Formulário -->
          <form @submit.prevent="createAccount" class="space-y-4">
            <!-- Senha -->
            <div>
              <label class="block text-sm font-medium mb-2"> Senha* </label>
              <Password
                v-model="cadastro.senha"
                placeholder="Crie uma senha"
                toggleMask
                :feedback="false"
              />
              <p class="text-xs text-white/50 mt-1">
                Mínimo de 8 caracteres, sendo ao menos 1 letra maiúscula, 1
                minúscula e 1 caractere especial.
              </p>
            </div>

            <!-- Confirmar senha -->
            <div>
              <label class="block text-sm font-medium mb-2">
                Confirmar senha*
              </label>
              <Password
                v-model="cadastro.confirmarSenha"
                placeholder="Digite novamente"
                toggleMask
                :feedback="false"
              />
            </div>

            <RecaptchaCheckbox
              ref="registerCaptcha"
              @verified="handleRegisterCaptchaVerified"
              @expired="handleRegisterCaptchaExpired"
              @error="handleRegisterCaptchaError"
            />
            <p
              v-if="captchaMessage"
              class="text-amber-200 text-sm font-medium"
              role="alert"
            >
              {{ captchaMessage }}
            </p>

            <!-- Botões -->
            <Button
              type="submit"
              :loading="disableBtnLogin"
              :disabled="disableBtnLogin || !captchaToken"
              label="Concluir cadastro"
              class="w-full btn-dynamic-color"
            />

            <button
              type="button"
              @click="voltar()"
              class="w-full h-12 rounded-xl border border-white/30 text-white font-semibold text-base hover:bg-white/10 transition-colors"
            >
              Voltar
            </button>
          </form>

          <!-- Footer -->
          <div class="mt-6 text-center mb-4">
            <p class="text-sm text-white/60">
              Já possui uma conta?
              <a
                :href="loginLink"
                class="font-medium text-white/80 hover:text-white hover:underline transition-colors"
              >
                Entrar
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api/axios";
import { mapGetters } from "vuex";
import SHA256 from "crypto-js/sha256";
import BackgroundCustomizer from "@/components/BackgroundCustomizer.vue";
import CompanyLogo from "@/components/CompanyLogo.vue";
import RecaptchaCheckbox from "@/components/RecaptchaCheckbox.vue";
import { useTheme } from "@/composables/useTheme";
import { useEditorMode } from "@/composables/useEditorMode";

export default {
  name: "CadastroFinalizacao",
  components: {
    BackgroundCustomizer,
    CompanyLogo,
    RecaptchaCheckbox,
  },
  setup() {
    const { isDark, toggleTheme } = useTheme();
    const { editorModeActive, toggleEditorMode, initEditorMode } =
      useEditorMode();

    initEditorMode();

    return {
      isDark,
      toggleTheme,
      editorModeActive,
      toggleEditorMode,
    };
  },
  computed: {
    ...mapGetters({
      account: "GET_CREATE_ACCOUNT",
    }),
    detectedDevice() {
      const width = window.innerWidth;
      if (width < 768) return "mobile";
      if (width < 1024) return "tablet";
      return "desktop";
    },
    isFormStylingActive() {
      return this.formStylingConfig?.enabled?.[this.detectedDevice] || false;
    },
    currentFormConfig() {
      if (!this.isFormStylingActive) return null;
      return this.formStylingConfig?.[this.detectedDevice] || null;
    },
    formContainerStyle() {
      if (!this.isFormStylingActive || !this.currentFormConfig) return {};
      const { backgroundColor, opacity, width, minWidth, maxWidth, margin } =
        this.currentFormConfig;
      return {
        backgroundColor: this.hexToRgba(backgroundColor, opacity / 100),
        backdropFilter: "blur(16px)",
        borderRadius: "1rem",
        padding: "2rem",
        width: width || "100%",
        minWidth: minWidth || "0px",
        maxWidth: maxWidth || "600px",
        marginLeft: margin || "0rem",
        marginRight: margin || "0rem",
      };
    },
    formContainerClass() {
      if (!this.isFormStylingActive || !this.currentFormConfig) return "";
      const { alignment } = this.currentFormConfig;
      return alignment === "left"
        ? "mr-auto"
        : alignment === "right"
        ? "ml-auto"
        : "mx-auto";
    },
    parentContainerClass() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return "items-center mx-auto";
      }
      const { alignment } = this.currentFormConfig;
      if (alignment === "left") {
        return "items-start";
      } else if (alignment === "right") {
        return "items-end";
      } else {
        return "items-center mx-auto";
      }
    },
    parentContainerStyle() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return {};
      }
      const { alignment, alignmentOffset } = this.currentFormConfig;
      const style = {};

      if (alignment === "left") {
        style.paddingLeft = `${alignmentOffset * 0.25}rem`;
      } else if (alignment === "right") {
        style.paddingRight = `${alignmentOffset * 0.25}rem`;
      }

      return style;
    },
    childContainerWidthClass() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return "w-full";
      }
      return "";
    },
    shouldInvertLogo() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return false;
      }
      return this.currentFormConfig.logoInvert;
    },
    dynamicTextStyles() {
      if (!this.isFormStylingActive || !this.currentFormConfig) return "";
      const config = this.currentFormConfig;
      return `
        .form-container {
          --ob-button-hover-bg: ${this.hexToRgba(
            config.buttonColorType1,
            config.buttonOpacityType1 / 100,
          )};
        }
        .form-container {
          background-color: ${this.hexToRgba(
            config.backgroundColor,
            config.opacity / 100,
          )} !important;
          backdrop-filter: blur(16px) !important;
        }
        .form-container h1, .form-container h2, .form-container h3,
        .form-container h4, .form-container h5, .form-container h6,
        .form-container span, .form-container label, .form-container p {
          color: ${config.textColor} !important;
        }
        .form-container input:enabled, .form-container select:enabled, .form-container textarea:enabled {
          background-color: ${config.inputBgEnabled} !important;
          color: ${config.inputTextColor} !important;
        }
        .form-container input:disabled, .form-container select:disabled, .form-container textarea:disabled {
          background-color: ${config.inputBgDisabled} !important;
        }
        .login-glass .form-container label,
        .login-glass .form-container p,
        .login-glass label,
        .login-glass p {
          color: ${config.textColor} !important;
          background: none !important;
          padding: 0 !important;
        }
        .login-glass .form-container .p-inputtext:enabled,
        .login-glass .form-container input.p-inputtext:enabled,
        .login-glass .form-container input.p-component.p-inputtext:enabled,
        .login-glass .form-container .p-password input.p-inputtext:enabled,
        .login-glass .form-container .p-password input.p-component.p-inputtext:enabled,
        .login-glass .form-container .p-dropdown:enabled,
        .login-glass .form-container select:enabled,
        .login-glass .form-container textarea:enabled,
        .login-glass .p-inputtext:enabled,
        .login-glass input.p-inputtext:enabled,
        .login-glass input.p-component.p-inputtext:enabled,
        .login-glass .p-password input.p-inputtext:enabled,
        .login-glass .p-password input.p-component.p-inputtext:enabled,
        .dark .login-glass .p-dropdown,
        .dark .login-glass .p-dropdown.p-component,
        .dark .login-glass .p-select,
        .dark .login-glass .p-select.p-component,
        .dark .login-glass span.p-dropdown.p-component,
        .login-glass .p-dropdown,
        .login-glass .p-dropdown.p-component,
        .login-glass .p-select,
        .login-glass .p-select.p-component {
          background: ${config.inputBgEnabled} !important;
          background-color: ${config.inputBgEnabled} !important;
          color: ${config.inputTextColor} !important;
        }
        .login-glass .form-container .p-inputtext:disabled,
        .login-glass .form-container input.p-inputtext:disabled,
        .login-glass .form-container input.p-component.p-inputtext:disabled,
        .login-glass .form-container .p-password input.p-inputtext:disabled,
        .login-glass .form-container .p-password input.p-component.p-inputtext:disabled,
        .login-glass .form-container .p-dropdown:disabled,
        .login-glass .form-container select:disabled,
        .login-glass .form-container textarea:disabled,
        .login-glass .p-inputtext:disabled,
        .login-glass input.p-inputtext:disabled,
        .login-glass input.p-component.p-inputtext:disabled,
        .login-glass .p-password input.p-inputtext:disabled,
        .login-glass .p-password input.p-component.p-inputtext:disabled {
          background: ${config.inputBgDisabled} !important;
        }
        .login-glass .form-container .p-inputtext,
        .login-glass .form-container input.p-inputtext,
        .login-glass .form-container .p-password input,
        .login-glass .p-inputtext,
        .login-glass input.p-inputtext,
        .login-glass input.p-component.p-inputtext,
        .login-glass .p-password input,
        .login-glass input,
        .login-glass .p-dropdown .p-dropdown-label,
        .login-glass .p-dropdown .p-dropdown-label.p-placeholder,
        .login-glass .p-select .p-select-label,
        .login-glass .p-select .p-select-label.p-placeholder {
          color: ${config.inputTextColor} !important;
        }
        .login-glass .form-container .p-inputtext::placeholder,
        .login-glass .form-container input.p-inputtext::placeholder,
        .login-glass .form-container .p-password input::placeholder,
        .login-glass .p-inputtext::placeholder,
        .login-glass input.p-inputtext::placeholder,
        .login-glass input::placeholder {
          color: ${config.inputTextColor} !important;
          opacity: 0.6 !important;
        }
        .form-container a,
        .form-container ~ div a,
        .form-container ~ .text-center a {
          color: ${config.textColor} !important;
        }
        .form-container button:not([type="submit"]),
        .form-container ~ button:not([type="submit"]),
        button.flex.items-center {
          color: ${config.textColor} !important;
        }
        .form-container button[type="submit"],
        .form-container button.w-full,
        .form-container button.btn-dynamic-color,
        .form-container .p-button:not(.p-button-text):not(.p-button-outlined),
        .form-container .p-button:not(.p-button-text):not(.p-button-outlined) .p-button-label,
        .form-container .p-button:not(.p-button-text):not(.p-button-outlined) span {
          color: ${config.buttonTextColor} !important;
        }
        .form-container button[type="submit"],
        .form-container button.w-full:not(.border),
        .form-container button.btn-dynamic-color,
        .login-glass .p-button:not(.p-button-text):not(.p-button-outlined),
        .login-glass .form-container .p-button:not(.p-button-text):not(.p-button-outlined) {
          background-color: ${this.hexToRgba(
            config.buttonColorType1,
            config.buttonOpacityType1 / 100,
          )} !important;
          border-color: ${this.hexToRgba(
            config.buttonColorType1,
            config.buttonOpacityType1 / 100,
          )} !important;
        }
        .form-container button[type="submit"]:hover,
        .form-container button.w-full:not(.border):hover,
        .form-container button.btn-dynamic-color:hover,
        .login-glass .p-button:not(.p-button-text):not(.p-button-outlined):hover,
        .login-glass .form-container .p-button:not(.p-button-text):not(.p-button-outlined):hover,
        .p-button:not(.p-button-text):not(.p-button-outlined):hover .p-button-label {
          color: ${config.buttonHoverColor} !important;
        }
        button.w-full.border,
        button.h-12.border {
          background-color: ${this.hexToRgba(
            config.buttonColorType2,
            config.buttonOpacityType2 / 100,
          )} !important;
          border-color: ${config.buttonColorType2} !important;
        }
      `;
    },
  },
  data() {
    return {
      nomeEmpresa: "",
      cadastro: {
        senha: null,
        confirmarSenha: null,
      },
      disableBtnLogin: false,
      captchaToken: "",
      captchaMessage: "",
      permitirAvancar: false,
      customBackgroundStyle: null,
      customButtonColor: null,
      isBackgroundReady: false,
      formStylingConfig: null,
      showBackgroundCustomizer: false,
      logoPressTimer: null,
      themeClickCount: 0,
      themeClickTimer: null,
      secretInput: "",
      showSecretInput: false,
    };
  },
  mounted() {
    // Verifica se editor mode está ativo
    if (this.editorModeActive) {
      this.showBackgroundCustomizer = true;
    }
    this.nomeEmpresa = process.env.VUE_APP_NOME_EMPRESA;
    this.disableBtnLogin = false;
  },
  methods: {
    handleRegisterCaptchaVerified(token) {
      this.captchaToken = token;
      this.captchaMessage = "";
    },
    handleRegisterCaptchaExpired() {
      this.captchaToken = "";
      this.captchaMessage =
        "A verificação expirou. Confirme novamente para concluir o cadastro.";
    },
    handleRegisterCaptchaError() {
      this.captchaToken = "";
      this.captchaMessage =
        "Não foi possível concluir a verificação de segurança. Tente novamente.";
    },
    resetRegisterCaptcha(message = "") {
      this.captchaToken = "";
      this.captchaMessage = message;
      this.$nextTick(() => this.$refs.registerCaptcha?.reset());
    },
    hexToRgba(hex, alpha) {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    handleFormStyling(config) {
      this.formStylingConfig = config;
    },
    handleBackgroundChange(style) {
      this.customBackgroundStyle = style;
      this.isBackgroundReady = true;
    },
    handleButtonColorChange(color) {
      this.customButtonColor = color;
    },
    handleThemeClick() {
      this.toggleTheme();

      // Sincroniza o modo interno do form styling com o tema visual
      if (this.$refs.backgroundCustomizer) {
        const newMode = this.isDark ? "dark" : "light";
        this.$refs.backgroundCustomizer.syncModeWithTheme(newMode);
      }

      this.themeClickCount++;

      if (this.themeClickTimer) {
        clearTimeout(this.themeClickTimer);
      }

      this.themeClickTimer = setTimeout(() => {
        this.themeClickCount = 0;
      }, 2000);

      if (this.themeClickCount === 3) {
        this.showSecretInput = true;
        this.secretInput = "";
        this.themeClickCount = 0;

        this.$nextTick(() => {
          if (this.$refs.secretInput) {
            this.$refs.secretInput.focus();
          }
        });

        setTimeout(() => {
          if (this.secretInput === "") {
            this.showSecretInput = false;
          }
        }, 5000);
      }
    },
    checkSecretCode() {
      const input = this.secretInput.toLowerCase();

      if (input === "editor") {
        this.toggleEditorMode();
        this.showBackgroundCustomizer = this.editorModeActive;
        this.showSecretInput = false;
        this.secretInput = "";
      }

      if (input.length > 6 && input !== "editor") {
        this.showSecretInput = false;
        this.secretInput = "";
      }
    },
    startLogoPress() {
      // Apenas no mobile e para empresas DNWAYS
      if (window.innerWidth >= 1024) {
        // Desktop - sempre mostra
        this.showBackgroundCustomizer = true;
        return;
      }

      const isDnways =
        this.nomeEmpresa === "DNWAYS" ||
        this.nomeEmpresa === "DNWAYS_DEMO_Y" ||
        this.nomeEmpresa === "DNWAYS_DEMO_X";

      if (!isDnways) return;

      // Mobile - requer 5 segundos de press
      this.logoPressTimer = setTimeout(() => {
        this.showBackgroundCustomizer = true;
      }, 5000);
    },
    endLogoPress() {
      if (this.logoPressTimer) {
        clearTimeout(this.logoPressTimer);
        this.logoPressTimer = null;
      }
    },
    limparCadastro() {
      this.cadastro.senha = null;
      this.cadastro.confirmarSenha = null;
    },
    validarConcluirCadastro() {
      if (this.cadastro.senha == null || this.cadastro.senha == "") {
        this.$alert({
          icon: "error",
          title: "Oops...",
          text: `Necessário preencher a senha`,
        });
        return false;
      }
      if (this.cadastro.senha != this.cadastro.confirmarSenha) {
        this.$alert({
          icon: "error",
          title: "Oops...",
          text: `Senha e confirmação de senha precisam ser iguais`,
        });
        return false;
      }
      return true;
    },
    validarSenha(senha) {
      if (!senha) return false;

      const temLetraMaiuscula = /[A-Z]/.test(senha);
      const temLetraMinuscula = /[a-z]/.test(senha);
      const temNumero = /[0-9]/.test(senha);
      const tamanhoMinimo = senha.length >= 8;

      return (
        temLetraMaiuscula && temLetraMinuscula && temNumero && tamanhoMinimo
      );
    },
    async createAccount() {
      this.disableBtnLogin = true;
      if (!this.validarConcluirCadastro()) {
        this.disableBtnLogin = false;
        return;
      }
      if (this.cadastro == null) {
        this.disableBtnLogin = false;
        return;
      }
      if (this.cadastro.senha != this.cadastro.confirmarSenha) {
        this.disableBtnLogin = false;
        return;
      }

      if (!this.validarSenha(this.cadastro.senha)) {
        this.$alert({
          icon: "error",
          title: "Oops...",
          text: `Senha informada não atende aos critérios. É necessário possuir o mínimo de 8 caracteres, sendo ao menos 1 letra maiúscula, 1 minúscula e 1 caractere especial.`,
        });
        this.disableBtnLogin = false;
        return;
      }

      if (!this.captchaToken) {
        this.captchaMessage =
          "Conclua a verificação de segurança para finalizar o cadastro.";
        this.disableBtnLogin = false;
        return;
      }

      var cadastroFinal = {
        TipoCadastro: this.account.selectedTipoCliente,
        NomeRazaoSocial: this.account.nomeRazaoSocial,
        CpfCnpj:
          this.account.selectedTipoCliente == "1"
            ? this.account.cpf
            : this.account.cnpj,
        Email: this.account.email,
        Password: SHA256(this.cadastro.senha).toString(),
        Apelido: this.account.apelido,
        DataConstituicao: this.account.dataConstituicao,
        DadosEndereco: this.account.dadosEndereco,
        AssessorId: this.account.assessorId || null,
        AssessorPossuiIndicacao:
          this.account.assessorPossuiIndicacao == "S"
            ? true
            : this.account.assessorPossuiIndicacao == "N"
            ? false
            : null,
        CaptchaToken: this.captchaToken,
      };
      this.captchaToken = "";
      await api
        .post("/register", cadastroFinal)
        .then((retorno) => {
          this.disableBtnLogin = false;
          if (!retorno.data) {
            this.resetRegisterCaptcha(
              "Confirme novamente a verificação para tentar concluir o cadastro.",
            );
            if (
              typeof retorno.data?.[0] === "string" &&
              retorno.data[0].includes("Password")
            ) {
              this.$alert({
                icon: "error",
                title: "Oops...",
                text: `Senha informada não atende aos critérios. É necessário possuir o mínimo de 8 caracteres, sendo ao menos 1 letra maiúscula, 1 minúscula e 1 caractere especial.`,
              });
            } else {
              this.$alert({
                icon: "error",
                title: "Oops...",
                text: `Falha na criação da conta`,
              });
            }
          } else {
            this.$store.commit("SET_CREATE_ACCOUNT", []);
            this.limparCadastro();
            this.$store.commit("SET_MODAL_ADESAO", true);
            this.$alert({
              icon: "success",
              title: "Cadastro realizado!",
              text: `Acesse o e-mail cadastrado para confirmar seu acesso.`,
            }).then((result) => {
              if (result.value) {
                this.$router.push({
                  path: "/login",
                });
              }
            });
          }
        })
        .catch((error) => {
          this.disableBtn = false;
          this.disableBtnLogin = false;

          const captchaRejected = error?.response?.status === 403;
          this.resetRegisterCaptcha(
            captchaRejected
              ? "A verificação de segurança não foi aceita. Confirme novamente."
              : "Confirme novamente a verificação para tentar concluir o cadastro.",
          );

          if (
            error?.response?.data?.data?.[0] ==
            "Password: Verifique o padrão da senha"
          ) {
            this.$alert({
              icon: "error",
              title: "Oops...",
              text: `Senha informada não atende aos critérios. É necessário possuir o mínimo de 8 caracteres, sendo ao menos 1 letra maiúscula, 1 minúscula e 1 caractere especial.`,
            });
          } else {
            this.$alert({
              icon: captchaRejected ? "warning" : "error",
              title: captchaRejected ? "Verificação necessária" : "Oops...",
              text:
                error?.response?.data?.descricao ||
                "Não foi possível concluir o cadastro. Tente novamente.",
            });
          }
        });
    },
    voltar() {
      this.$router.replace({ name: "CadastroInicio" });
    },
  },
  // watch: {
  //     // consultarPermissaoAvancar() {
  //     //     this.permitirAvancar = false;
  //     //     if (this.cadastro.selectedTipoCliente == 'PF') {
  //     //         // Para Pessoa Física, nome completo e CPF são obrigatórios
  //     //         if (this.cadastro.nomeCompleto && this.cadastro.nomeCompleto.length > 5 && this.validarCPF(this.cadastro.CPF)) {
  //     //             this.permitirAvancar = true;
  //     //         }
  //     //     } else if (this.cadastro.selectedTipoCliente == 'PJ' || this.cadastro.selectedTipoCliente == 'FU') {
  //     //         // Para Pessoa Jurídica ou Funcionário, razão social e CNPJ são obrigatórios
  //     //         if (this.cadastro.razaoSocial && this.cadastro.CNPJ && this.validarCNPJ(this.cadastro.CNPJ)) {
  //     //             this.permitirAvancar = true;
  //     //         }
  //     //     }
  //     // }
  // }
};
</script>
